/**
 * Rolagem Automática do Painel, por páginas.
 *
 * Aqui se decide onde cada página começa, a partir da geometria já medida. A
 * medição fica isolada em `measurePageOffsets`, a única função que toca o DOM:
 * o resto é puro e testável sem layout, que o jsdom não calcula.
 */

/** Tempo que cada página fica parada antes de avançar. */
export const PAGE_DWELL_MS = 30000;

/** Duração do deslizamento entre duas páginas. */
export const PAGE_TRANSITION_MS = 1200;

/**
 * Tempo sem interação até a rolagem retomar sozinha. Quem rolou com a roda,
 * tocou ou clicou está lendo algo: tirar a tela da pessoa no meio seria pior do
 * que não ter rolagem nenhuma.
 */
export const RESUME_AFTER_IDLE_MS = 60000;

/** Arredondamento de medida: meio pixel de diferença não é linha cortada. */
const TOLERANCE_PX = 1;

/**
 * Reúne as caixas dos cartões em linhas da grade.
 *
 * @param {Object[]} boxes `{ top, bottom }` de cada cartão, relativos ao
 *   conteúdo rolável.
 * @returns {Object[]} Linhas `{ top, bottom }`, de cima para baixo; o fundo da
 *   linha é o do cartão mais alto dela.
 */
export function groupRows(boxes) {
  const sorted = (boxes || [])
    .slice()
    .sort((left, right) => left.top - right.top);

  return sorted.reduce((rows, box) => {
    const last = rows[rows.length - 1];

    if (last && Math.abs(last.top - box.top) <= TOLERANCE_PX) {
      return rows
        .slice(0, -1)
        .concat([{ top: last.top, bottom: Math.max(last.bottom, box.bottom) }]);
    }

    return rows.concat([{ top: box.top, bottom: box.bottom }]);
  }, []);
}

/**
 * Onde cada página começa.
 *
 * A próxima página começa na primeira linha que não coube inteira na atual: a
 * linha cortada embaixo vira a primeira da tela seguinte, e nenhum cartão
 * aparece pela metade no topo. Linha mais alta que a janela não tem como caber
 * — ali a página avança uma janela inteira. A última página encosta no fim do
 * conteúdo, em vez de deixar tela vazia embaixo.
 *
 * @param {Object[]} rows Saída de `groupRows`.
 * @param {Number} viewportHeight Altura visível do container.
 * @param {Number} contentHeight Altura total rolável (`scrollHeight`).
 * @param {Number} [topInset] Respiro do topo do container: a linha que abre a
 *   página fica no mesmo lugar em que a primeira linha fica.
 * @returns {Number[]} Offsets de `scrollTop`, a partir de 0. Um só quando não
 *   há rolagem.
 */
export function getPageOffsets(
  rows,
  viewportHeight,
  contentHeight,
  topInset = 0,
) {
  const maxOffset = Math.max(contentHeight - viewportHeight, 0);
  const offsets = [0];

  if (viewportHeight <= 0 || maxOffset <= TOLERANCE_PX) {
    return offsets;
  }

  let current = 0;
  while (current < maxOffset) {
    const visibleBottom = current + viewportHeight;
    const cut = (rows || []).find(
      (row) => row.bottom > visibleBottom + TOLERANCE_PX,
    );
    const rowStart = cut ? cut.top - topInset : visibleBottom;
    const start = rowStart > current + TOLERANCE_PX ? rowStart : visibleBottom;
    const next = Math.min(start, maxOffset);

    offsets.push(next);
    current = next;
  }

  return offsets;
}

/**
 * Página em que a rolagem está: a última cujo início já passou. Serve para
 * retomar de onde a pessoa deixou, e não do topo.
 *
 * @param {Number[]} offsets Saída de `getPageOffsets`.
 * @param {Number} scrollTop Posição atual.
 * @returns {Number} Índice da página.
 */
export function findPageIndex(offsets, scrollTop) {
  return (offsets || []).reduce(
    (found, offset, index) => (scrollTop + TOLERANCE_PX >= offset ? index : found),
    0,
  );
}

/**
 * Aceleração e freio suaves: a página sai devagar, corre no meio e pousa sem
 * tranco — o movimento de uma apresentação, não de um scroll de mouse.
 *
 * @param {Number} progress Fração do tempo da transição, entre 0 e 1.
 * @returns {Number} Fração da distância percorrida.
 */
export function easeInOutCubic(progress) {
  const time = Math.max(0, Math.min(1, progress));

  if (time < 0.5) {
    return 4 * time * time * time;
  }

  // Espelho da primeira metade: o que falta percorrer, ao cubo.
  const remaining = 2 - 2 * time;
  const remainingCubed = remaining * remaining * remaining;

  return 1 - remainingCubed / 2;
}

/**
 * Mede o container e os cartões e devolve as páginas.
 *
 * Os cartões são os filhos do primeiro elemento do container — a grade. As
 * posições saem relativas ao conteúdo rolável, somando o `scrollTop` atual.
 *
 * @param {HTMLElement} viewport Container que rola.
 * @returns {Number[]} Saída de `getPageOffsets`.
 */
export function measurePageOffsets(viewport) {
  if (!viewport) {
    return [0];
  }

  const grid = viewport.firstElementChild;
  const viewportTop = viewport.getBoundingClientRect().top;
  const boxes = Array.from(grid ? grid.children : []).map((element) => {
    const rect = element.getBoundingClientRect();
    const top = rect.top - viewportTop + viewport.scrollTop;

    return { top, bottom: top + rect.height };
  });
  const topInset = parseFloat(window.getComputedStyle(viewport).paddingTop) || 0;

  return getPageOffsets(
    groupRows(boxes),
    viewport.clientHeight,
    viewport.scrollHeight,
    topInset,
  );
}
